/**
 * How to READ a file, decided from its name — and the two parsers that decision needs.
 *
 *   owner, 2026-08-07: text, code, csv, json, markdown, image, video, audio 都要能夠
 *   正確的依照該顯示的方式顯示. 該可以播放的方式播放.
 *
 * The edge classifies into image / video / audio / markdown / text / other. That is the
 * right split for TRANSPORT — it decides base64 versus utf8 — and too coarse for
 * reading: JSON, CSV and source code all arrive as `text` and none of them should be
 * rendered as a wall of monospace.
 */

/**
 * The mime a playable file's NAME implies, for the one road where the machine does not
 * declare it: bytes paged in slices arrive with no type, and a typeless blob is what
 * WebKit refuses to play. Only the names the edge itself calls image/video/audio.
 */
const MEDIA_MIMES: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp',
  bmp: 'image/bmp', svg: 'image/svg+xml', avif: 'image/avif', ico: 'image/x-icon', heic: 'image/heic',
  mp4: 'video/mp4', m4v: 'video/mp4', webm: 'video/webm', ogv: 'video/ogg', ogg: 'video/ogg',
  mov: 'video/quicktime', mkv: 'video/x-matroska',
  mp3: 'audio/mpeg', wav: 'audio/wav', wave: 'audio/wav', m4a: 'audio/mp4', aac: 'audio/aac',
  flac: 'audio/flac', oga: 'audio/ogg', opus: 'audio/ogg', weba: 'audio/webm', wma: 'audio/x-ms-wma',
  aiff: 'audio/aiff', aif: 'audio/aiff',
}
export function mediaMime(name: string): string | undefined {
  return MEDIA_MIMES[name.toLowerCase().split('.').pop() ?? '']
}

/**
 * A PDF, by its name.
 *
 * The edge calls it `other` — its split is about TRANSPORT, and a PDF travels as bytes
 * like anything else it has no player for. But every browser this app runs in has a PDF
 * renderer of its own, so the viewer can show one (owner, 2026-10-12: 在 File Browser 里面
 * 打开 PDF, 好像没有预览的功能). Decided here, from the name, like the rest of this file.
 */
export const PDF_MIME = 'application/pdf'
export function isPdf(name: string): boolean {
  return name.toLowerCase().endsWith('.pdf')
}

/** What a `text` file actually is, once its extension is consulted. */
export type Sub = 'json' | 'csv' | 'tsv' | 'code' | 'plain'

const CODE_EXTS = new Set([
  'py', 'pyw', 'pyi', 'js', 'mjs', 'cjs', 'ts', 'tsx', 'jsx', 'sh', 'bash', 'zsh', 'fish',
  'ps1', 'bat', 'cmd', 'c', 'h', 'cpp', 'cc', 'cxx', 'hpp', 'hh', 'go', 'rs', 'java', 'kt',
  'kts', 'swift', 'rb', 'php', 'pl', 'pm', 'lua', 'r', 'jl', 'dart', 'scala', 'clj', 'ex',
  'exs', 'erl', 'hs', 'yaml', 'yml', 'toml', 'ini', 'cfg', 'conf', 'env', 'properties',
  'diff', 'patch', 'sql', 'css', 'scss', 'less', 'html', 'xml', 'vue', 'svelte', 'gradle',
  'make', 'mk', 'cmake', 'dockerfile', 'graphql', 'gql', 'proto', 'tf', 'hcl', 'nix',
])

/** Files with no extension that are still code, by convention. */
const CODE_NAMES = new Set([
  'makefile', 'dockerfile', 'jenkinsfile', 'rakefile', 'gemfile', 'procfile', 'brewfile',
  'cmakelists.txt', 'justfile', 'vagrantfile',
])

export function subKind(name: string): Sub {
  const lower = name.toLowerCase()
  const ext = lower.includes('.') ? (lower.split('.').pop() ?? '') : ''
  if (ext === 'json' || ext === 'json5' || ext === 'jsonc') return 'json'
  if (ext === 'csv') return 'csv'
  if (ext === 'tsv') return 'tsv'
  // `Makefile` and `Dockerfile` carry no extension; without this they read as prose.
  if (CODE_NAMES.has(lower)) return 'code'
  return CODE_EXTS.has(ext) ? 'code' : 'plain'
}

/**
 * Split delimited text, honouring quotes.
 *
 * The previous viewer split on the separator and said so in a comment: "quoted fields
 * containing the separator will render wrong, and that is a known limit". It is a
 * limit that silently shifts every column after the offending one, so the table stays
 * plausible and stops being true — the worst way for a data view to fail. RFC 4180:
 * quotes wrap a field, a doubled quote inside one is a literal quote, and a newline
 * inside quotes does NOT end the row.
 */
export function parseDelimited(text: string, sep: string, maxRows = 2000): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let quoted = false
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!
    if (quoted) {
      if (c === '"') {
        // A doubled quote is one literal quote; a single one closes the field.
        if (text[i + 1] === '"') { field += '"'; i++ } else quoted = false
      } else field += c
      continue
    }
    if (c === '"' && field === '') { quoted = true; continue }
    if (c === sep) { row.push(field); field = ''; continue }
    if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++
      row.push(field)
      field = ''
      // A trailing newline must not become a row of one empty cell.
      if (row.length > 1 || row[0] !== '') rows.push(row)
      row = []
      if (rows.length >= maxRows) return rows
      continue
    }
    field += c
  }
  if (field !== '' || row.length) {
    row.push(field)
    rows.push(row)
  }
  return rows
}

/* ── highlighting ─────────────────────────────────────────────────────────── */

export type Tok = { t: 'str' | 'num' | 'kw' | 'com' | 'txt'; v: string }

const KEYWORDS = new Set([
  'const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while', 'break',
  'continue', 'class', 'new', 'this', 'import', 'from', 'export', 'default', 'async',
  'await', 'try', 'catch', 'finally', 'throw', 'typeof', 'instanceof', 'in', 'of',
  'true', 'false', 'null', 'undefined', 'def', 'elif', 'lambda', 'pass', 'None', 'True',
  'False', 'and', 'or', 'not', 'with', 'as', 'yield', 'fn', 'pub', 'struct', 'enum',
  'impl', 'trait', 'match', 'mut', 'use', 'type', 'interface', 'extends', 'implements',
  'public', 'private', 'protected', 'static', 'void', 'int', 'string', 'bool', 'float',
  'package', 'func', 'go', 'defer', 'select', 'switch', 'case', 'end', 'do', 'then', 'fi',
  'echo', 'local', 'set', 'unset', 'elsif', 'module', 'require',
])

/**
 * A deliberately small lexer: strings, comments, numbers, keywords, everything else.
 *
 * NOT a parser, and not per-language. A real highlighter is a dependency, and this
 * repo's one hard rule about dependencies is that an artifact must stay
 * self-contained. Five token classes is what makes code scannable — finding the string
 * literal and the comment is most of it — and being approximate is fine as long as it
 * cannot LOSE text: every branch below appends to a token, so concatenating the output
 * reproduces the input exactly. `highlight.test.ts` pins that.
 */
export function highlight(src: string): Tok[] {
  const out: Tok[] = []
  const push = (t: Tok['t'], v: string) => {
    const last = out[out.length - 1]
    if (last && last.t === t) last.v += v
    else out.push({ t, v })
  }
  let i = 0
  while (i < src.length) {
    const c = src[i]!
    const two = src.slice(i, i + 2)

    // Comments: // … , # … , -- … to end of line; /* … */ across lines.
    if (two === '//' || two === '--' || (c === '#' && src[i + 1] !== '!')) {
      const nl = src.indexOf('\n', i)
      const end = nl === -1 ? src.length : nl
      push('com', src.slice(i, end))
      i = end
      continue
    }
    if (two === '/*') {
      const close = src.indexOf('*/', i + 2)
      const end = close === -1 ? src.length : close + 2
      push('com', src.slice(i, end))
      i = end
      continue
    }

    // Strings. Unterminated ones run to end of line rather than swallowing the file.
    if (c === '"' || c === "'" || c === '`') {
      let j = i + 1
      while (j < src.length) {
        if (src[j] === '\\') { j += 2; continue }
        if (src[j] === c) { j++; break }
        if (src[j] === '\n' && c !== '`') break
        j++
      }
      push('str', src.slice(i, j))
      i = j
      continue
    }

    if (c >= '0' && c <= '9') {
      let j = i
      while (j < src.length && /[0-9a-fA-FxX._]/.test(src[j]!)) j++
      push('num', src.slice(i, j))
      i = j
      continue
    }

    if (/[A-Za-z_$]/.test(c)) {
      let j = i
      while (j < src.length && /[A-Za-z0-9_$]/.test(src[j]!)) j++
      const word = src.slice(i, j)
      push(KEYWORDS.has(word) ? 'kw' : 'txt', word)
      i = j
      continue
    }

    push('txt', c)
    i++
  }
  return out
}
